/**
 * Lumina Specialty Config
 * ──────────────────────────────────────────────────────────────────────
 * Central map of Tier-1 specialties with vocabulary, example phrases,
 * common CPT codes, and suggested clinical signals. All data here is a
 * mock surface for Phase 1 — real LLM prompt tuning + EHR lookups plug
 * in later by consuming these same keys.
 *
 *   import { getSpecialty, SPECIALTIES } from '../../config/specialtyConfig';
 *   const cfg = getSpecialty(user.specialty);    // → { id, label, mode, vocab, … }
 */

/* Rendering modes for Clinical Focus:
   - 'soap'        : standard SOAP note panel (default)
   - 'mental'      : Mental Health / Psychiatry replaces SOAP with MSE + Risk + Scales
   - 'soap+obgyn'  : standard SOAP with an OB sidebar panel
   - 'soap+peds'   : standard SOAP with Growth & Development sidebar
   - 'surgical'    : pre-op / post-op / procedure-note toggle
*/

const commonSoap = {
  sample_assessment:
    'Well-controlled chronic conditions. No acute concerns today. Continue current management with routine follow-up.',
  sample_plan:
    'Continue current medications. Labs at next visit. Lifestyle counseling reinforced. Return in 3 months.',
};

const S = (obj) => ({ mode: 'soap', ...commonSoap, ...obj });

export const SPECIALTIES = [
  S({
    id: 'internal_medicine',
    label: 'Internal Medicine',
    short: 'IM',
    tagline: 'Adult primary care & chronic disease',
    vocab: ['HTN', 'HLD', 'T2DM', 'CKD stage', 'A1c', 'GFR', 'ASCVD risk'],
    example_phrases: [
      'HTN well-controlled on current regimen, SBP trending 118–126.',
      'T2DM — A1c 7.1%, at goal; continue metformin + SGLT2i.',
      'Lipid panel within target per ASCVD risk calculator.',
    ],
    common_cpts: [
      { code: '99213', desc: 'Est. patient · low complexity (15 min)' },
      { code: '99214', desc: 'Est. patient · moderate complexity (25 min)' },
      { code: 'G0439', desc: 'Annual Wellness Visit (subsequent)' },
    ],
    signals: [
      { tone: 'amber', text: 'HbA1c last checked 8 months ago — consider ordering.' },
      { tone: 'green', text: 'Colorectal cancer screening overdue for patient age 52.' },
    ],
    smart_prep: ['Last A1c', 'Last BP trend', 'Current antihypertensives', 'Last lipid panel'],
  }),
  S({
    id: 'family_medicine',
    label: 'Family Medicine',
    short: 'FM',
    tagline: 'Full-family primary care',
    vocab: ['AWV', 'HM screenings', 'Tdap', 'Flu', 'PHQ-9'],
    example_phrases: [
      'AWV complete; depression screen PHQ-9 = 2.',
      'Family Hx of early CAD — ASCVD risk 8.2%.',
      'Health maintenance up to date through age 45.',
    ],
    common_cpts: [
      { code: '99213', desc: 'Est. patient · 15 min' },
      { code: '99385', desc: 'New patient preventive (18–39)' },
      { code: 'G0438', desc: 'Annual Wellness Visit (initial)' },
    ],
    signals: [
      { tone: 'green', text: 'Flu vaccine not administered this season.' },
      { tone: 'blue',  text: 'Allergy review not mentioned in this note.' },
    ],
    smart_prep: ['Health maintenance status', 'Vaccines due', 'Last annual visit date'],
  }),
  S({
    id: 'cardiology',
    label: 'Cardiology',
    short: 'Cards',
    tagline: 'Heart failure, ACS, arrhythmia',
    vocab: ['EF', 'LVOTO', 'BNP', 'NT-proBNP', 'NYHA', 'AFib', 'CHF', 'stent'],
    example_phrases: [
      'EF 45% on last TTE; Class II CHF symptoms.',
      'BNP trending down from 820 → 410 after GDMT titration.',
      'Continues on quad therapy: ARNI, beta-blocker, SGLT2i, MRA.',
    ],
    common_cpts: [
      { code: '93306', desc: 'Echocardiogram with Doppler' },
      { code: '93000', desc: 'Electrocardiogram (ECG)' },
      { code: '99214', desc: 'Est. patient · moderate complexity' },
    ],
    signals: [
      { tone: 'amber', text: 'Echo not ordered in 12 months for EF < 50%.' },
      { tone: 'purple', text: 'Statin indicated per ACC/AHA guidelines — consider documenting.' },
    ],
    smart_prep: ['Last echo + EF', 'Last ECG', 'Current cardiac meds', 'Last BNP'],
  }),
  S({
    id: 'pulmonology',
    label: 'Pulmonology',
    short: 'Pulm',
    tagline: 'COPD, asthma, ILD',
    vocab: ['FEV1', 'FVC', 'FEV1/FVC ratio', 'peak flow', 'GOLD stage', 'accessory muscle use'],
    example_phrases: [
      'FEV1/FVC 0.62, GOLD stage B.',
      'Peak flow 320 L/min — near personal best.',
      'No accessory muscle use; clear to auscultation bilaterally.',
    ],
    common_cpts: [
      { code: '94010', desc: 'Spirometry' },
      { code: '94060', desc: 'Spirometry pre/post bronchodilator' },
      { code: '99214', desc: 'Est. patient · moderate complexity' },
    ],
    signals: [
      { tone: 'amber', text: 'PFTs older than 12 months — consider repeat spirometry.' },
      { tone: 'green', text: 'Pneumococcal vaccine due per CDC schedule.' },
    ],
    smart_prep: ['Last PFTs', 'Last O2 saturation', 'Current inhalers', 'Smoking status'],
  }),
  S({
    id: 'endocrinology',
    label: 'Endocrinology',
    short: 'Endo',
    tagline: 'Diabetes, thyroid, metabolic',
    vocab: ['A1c', 'TSH', 'T4', 'TPO Ab', 'GFR', 'CGM average'],
    example_phrases: [
      'A1c 8.3% — intensifying regimen with GLP-1.',
      'TSH 4.1 mIU/L on current levothyroxine.',
      'CGM 14-day GMI 7.6%, time-in-range 58%.',
    ],
    common_cpts: [
      { code: '83036', desc: 'HbA1c' },
      { code: '84443', desc: 'TSH' },
      { code: '99214', desc: 'Est. patient · moderate complexity' },
    ],
    signals: [
      { tone: 'amber', text: 'Microalbuminuria not checked in past year (T2DM).' },
      { tone: 'green', text: 'Annual retinal exam due.' },
    ],
    smart_prep: ['Last A1c trend', 'Current meds', 'Last thyroid panel', 'CGM data'],
  }),
  S({
    id: 'rheumatology', label: 'Rheumatology', short: 'Rheum',
    tagline: 'Autoimmune + inflammatory joint disease',
    vocab: ['DAS28', 'CDAI', 'ESR', 'CRP', 'tender joint count', 'ANA'],
    example_phrases: ['RA in low disease activity, DAS28 2.8.', 'ANA 1:160 speckled, ENA negative.'],
    common_cpts: [{ code: '99214', desc: 'Est. patient · moderate complexity' }, { code: '20610', desc: 'Arthrocentesis, major joint' }],
    signals: [{ tone: 'amber', text: 'TB screening recommended before next biologic refill.' }],
    smart_prep: ['Last DAS28', 'Current biologics', 'Joint imaging'],
  }),
  S({
    id: 'neurology', label: 'Neurology', short: 'Neuro',
    tagline: 'Headache, epilepsy, MS, stroke',
    vocab: ['MRS', 'NIHSS', 'headache frequency', 'seizure semiology', 'EEG'],
    example_phrases: ['Migraine with aura — 4 attacks/month.', 'Post-stroke mRS 1; continues DAPT × 90 days.'],
    common_cpts: [{ code: '99214', desc: 'Est. patient · moderate complexity' }, { code: '95812', desc: 'EEG 41–60 min' }],
    signals: [{ tone: 'amber', text: 'Preventive migraine therapy indicated (>4 attacks/mo).' }],
    smart_prep: ['Last MRI', 'Current neuro meds', 'Seizure diary'],
  }),
  S({
    id: 'oncology', label: 'Oncology', short: 'Onc',
    tagline: 'Solid-tumor + heme malignancy',
    vocab: ['ECOG', 'RECIST', 'chemo cycle', 'NCCN', 'performance status'],
    example_phrases: ['ECOG 1; cycle 4 of FOLFOX tolerated well.', 'RECIST partial response at 12-week restaging.'],
    common_cpts: [{ code: '99214', desc: 'Est. patient · moderate complexity' }, { code: '96413', desc: 'Chemo IV infusion, initial hour' }],
    signals: [{ tone: 'amber', text: 'Neutropenic precautions if ANC < 1.0 — consider G-CSF.' }],
    smart_prep: ['Last imaging', 'Current regimen + cycle', 'Last CBC/CMP'],
  }),
  S({
    id: 'nephrology', label: 'Nephrology', short: 'Neph',
    tagline: 'CKD, HTN, dialysis, transplant',
    vocab: ['eGFR', 'UACR', 'K+', 'phosphorus', 'PTH', 'CKD stage'],
    example_phrases: ['CKD stage 3b — eGFR 35, UACR 120.', 'Phos 5.1, on binder; iPTH 240.'],
    common_cpts: [{ code: '99214', desc: 'Est. patient · moderate complexity' }, { code: '90960', desc: 'ESRD monthly · 4+ visits' }],
    signals: [{ tone: 'amber', text: 'ACEi/ARB indicated for proteinuric CKD.' }],
    smart_prep: ['Last BMP', 'Proteinuria trend', 'Current renal meds'],
  }),
  S({
    id: 'hematology', label: 'Hematology', short: 'Heme',
    tagline: 'Anemia, clotting, malignancy',
    vocab: ['Hgb', 'platelet count', 'INR', 'ferritin', 'MCV'],
    example_phrases: ['Iron-deficiency anemia — Hgb 9.2, ferritin 14.'],
    common_cpts: [{ code: '99214', desc: 'Est. patient · moderate complexity' }, { code: '85025', desc: 'CBC w/ diff' }],
    signals: [{ tone: 'amber', text: 'GI eval recommended for new IDA in adult.' }],
    smart_prep: ['Last CBC', 'Ferritin + iron studies'],
  }),
  S({
    id: 'infectious_disease', label: 'Infectious Disease', short: 'ID',
    tagline: 'HIV, OPAT, antimicrobial stewardship',
    vocab: ['CD4', 'viral load', 'MIC', 'OPAT', 'empiric coverage'],
    example_phrases: ['HIV suppressed (VL <20), CD4 680.', 'OPAT day 14 of 42 — PICC functioning.'],
    common_cpts: [{ code: '99214', desc: 'Est. patient · moderate complexity' }, { code: '99212', desc: 'Est. patient · straightforward' }],
    signals: [{ tone: 'amber', text: 'Annual STI screening recommended for HIV+ patients.' }],
    smart_prep: ['Last VL/CD4', 'Current ART', 'Culture results'],
  }),
  S({
    id: 'pain_management', label: 'Pain Management', short: 'Pain',
    tagline: 'Chronic pain, interventional',
    vocab: ['VAS', 'functional limitation', 'opioid risk', 'PDMP', 'MME'],
    example_phrases: ['VAS 7/10, functional limitation moderate.', 'Opioid risk stratification low; PDMP reviewed.'],
    common_cpts: [{ code: '99214', desc: 'Est. patient · moderate complexity' }, { code: '64483', desc: 'Transforaminal ESI, lumbar' }],
    signals: [
      { tone: 'amber', text: 'PDMP check not documented before controlled substance refill.' },
      { tone: 'blue',  text: 'Opioid treatment agreement not on file.' },
    ],
    smart_prep: ['Pain diary', 'Recent imaging', 'MME total'],
  }),
  S({
    id: 'pmr', label: 'Physical Medicine & Rehab', short: 'PM&R',
    tagline: 'Rehabilitation, MSK, EMG',
    vocab: ['ROM', 'MMT', 'FIM', 'EMG', 'nerve conduction'],
    example_phrases: ['MMT 4/5 RUE; ROM WFL.', 'EMG shows mild C6 radiculopathy.'],
    common_cpts: [{ code: '99214', desc: 'Est. patient · moderate complexity' }, { code: '95910', desc: 'NCS 7–8 studies' }],
    signals: [{ tone: 'green', text: 'Home exercise program adherence not documented.' }],
    smart_prep: ['Functional assessment', 'Therapy progress notes'],
  }),
  S({
    id: 'orthopedics', label: 'Orthopedics', short: 'Ortho',
    mode: 'surgical',
    tagline: 'Musculoskeletal, surgical',
    vocab: ['ROM', 'crepitus', 'Lachman', 'McMurray', 'Spurling'],
    example_phrases: ['ROM knee 0–120°, no effusion.', 'Positive Lachman; MRI confirms ACL tear.'],
    common_cpts: [{ code: '99214', desc: 'Est. patient · moderate complexity' }, { code: '29881', desc: 'Knee arthroscopy, meniscectomy' }, { code: '27447', desc: 'Total knee arthroplasty' }],
    signals: [{ tone: 'purple', text: 'Adding joint-specific exam supports higher E/M level.' }],
    smart_prep: ['Last MRI/X-ray', 'Prior surgical history', 'PT progress'],
  }),
  S({
    id: 'dermatology', label: 'Dermatology', short: 'Derm',
    tagline: 'Skin, hair, nails',
    vocab: ['ABCDE criteria', 'dermoscopy', 'Breslow depth', 'erythematous', 'scaling'],
    example_phrases: ['Solitary 6mm pigmented lesion, ABCDE+.', 'Plaque psoriasis PASI 6 on left elbow.'],
    common_cpts: [{ code: '99213', desc: 'Est. patient · 15 min' }, { code: '11102', desc: 'Tangential biopsy' }, { code: '17000', desc: 'Destruction, AK, 1 lesion' }],
    signals: [{ tone: 'amber', text: 'Full-body skin check not documented in past 12 months.' }],
    smart_prep: ['Biopsy history', 'Prior treatments'],
  }),
  S({
    id: 'gastroenterology', label: 'Gastroenterology', short: 'GI',
    tagline: 'GI + liver',
    vocab: ['LFTs', 'FIB-4', 'endoscopy', 'colonoscopy prep', 'H. pylori'],
    example_phrases: ['LFTs trending down after stopping offending agent.', 'Colonoscopy clean prep; 2 small polyps, pathology pending.'],
    common_cpts: [{ code: '45378', desc: 'Colonoscopy, diagnostic' }, { code: '43239', desc: 'EGD with biopsy' }],
    signals: [{ tone: 'green', text: 'CRC screening due — consider colonoscopy vs Cologuard.' }],
    smart_prep: ['Last colonoscopy', 'LFT trend'],
  }),
  S({
    id: 'urology', label: 'Urology', short: 'Uro',
    mode: 'surgical',
    tagline: 'Urologic + surgical',
    vocab: ['PSA', 'IPSS', 'uroflow', 'post-void residual'],
    example_phrases: ['PSA 3.2, stable.', 'IPSS 12 — mild-to-moderate LUTS.'],
    common_cpts: [{ code: '52000', desc: 'Cystourethroscopy' }, { code: '76872', desc: 'US transrectal' }, { code: '99214', desc: 'Est. patient · moderate complexity' }],
    signals: [{ tone: 'amber', text: 'PSA velocity increasing — consider MRI prostate.' }],
    smart_prep: ['Last PSA', 'Urology imaging'],
  }),
  S({
    id: 'ent', label: 'ENT', short: 'ENT',
    mode: 'surgical',
    tagline: 'Ear, nose, throat',
    vocab: ['tympanogram', 'audiogram', 'nasendoscopy', 'stridor'],
    example_phrases: ['Type A tympanogram bilaterally.', 'Nasendoscopy normal larynx, no lesion.'],
    common_cpts: [{ code: '31231', desc: 'Nasal endoscopy' }, { code: '92557', desc: 'Comprehensive audiometry' }],
    signals: [{ tone: 'green', text: 'Hearing screen recommended for ≥65y.' }],
    smart_prep: ['Last audiogram', 'Recent imaging'],
  }),
  S({
    id: 'ophthalmology', label: 'Ophthalmology', short: 'Ophth',
    tagline: 'Eye, retina, glaucoma',
    vocab: ['IOP', 'visual acuity', 'OCT', 'cup-to-disc ratio'],
    example_phrases: ['IOP 18/17 on latanoprost.', 'OCT stable; no new drusen.'],
    common_cpts: [{ code: '92014', desc: 'Comprehensive exam, est.' }, { code: '92134', desc: 'OCT retina' }],
    signals: [{ tone: 'amber', text: 'Annual dilated exam due for diabetic patients.' }],
    smart_prep: ['Last visual field', 'IOP trend'],
  }),
  S({
    id: 'urgent_care', label: 'Urgent Care', short: 'UC',
    tagline: 'Acute, episodic care',
    vocab: ['WHO pain scale', 'Centor score', 'Strep', 'CURB-65'],
    example_phrases: ['Centor 3 — rapid strep +, Rx amoxicillin.', 'Ankle sprain grade II, Ottawa rules negative.'],
    common_cpts: [{ code: '99213', desc: 'Est. patient · 15 min' }, { code: '12001', desc: 'Simple repair, superficial' }],
    signals: [{ tone: 'blue', text: 'Follow-up plan with PCP not specified.' }],
    smart_prep: ['Reason for visit', 'Allergies', 'Current meds'],
  }),
  {
    id: 'obgyn', label: 'OB/GYN', short: 'OB/GYN',
    mode: 'soap+obgyn',
    tagline: 'Obstetrics + gynecology',
    vocab: ['G/P', 'LMP', 'EDD', 'fundal height', 'FHT', 'GBS', 'Bishop score'],
    example_phrases: [
      'G2P1 at 28+3 weeks; fundal height 29 cm; FHT 148 bpm.',
      'GBS status pending; 1-hour GCT 142.',
      'LMP WNL; Pap up to date.',
    ],
    common_cpts: [
      { code: '99214', desc: 'Est. patient · moderate complexity' },
      { code: '59400', desc: 'Routine OB care, global' },
      { code: '57170', desc: 'Diaphragm fitting' },
    ],
    signals: [
      { tone: 'green', text: 'Tdap indicated at 27–36 weeks gestation.' },
      { tone: 'amber', text: 'Gestational diabetes screen due (24–28 wks).' },
    ],
    smart_prep: ['LMP + EDD', 'Prenatal labs completed', 'Last US', 'Vaccines'],
    sample_assessment: 'Intrauterine pregnancy at 28+3, reassuring fetal status, routine prenatal care.',
    sample_plan: 'Tdap today, 1-hour GCT ordered, return in 2 weeks for routine prenatal visit.',
  },
  {
    id: 'pediatrics', label: 'Pediatrics', short: 'Peds',
    mode: 'soap+peds',
    tagline: 'Newborn through adolescent',
    vocab: ['weight-for-age', 'BMI %ile', 'CDC milestones', 'ACIP schedule'],
    example_phrases: [
      '95th percentile weight-for-age; length 65th percentile.',
      'Developmental milestones on track per CDC 18-month checklist.',
      'ACIP-recommended vaccines up to date.',
    ],
    common_cpts: [
      { code: '99392', desc: 'Preventive, est. (1–4y)' },
      { code: '99393', desc: 'Preventive, est. (5–11y)' },
      { code: '90471', desc: 'Immunization admin' },
    ],
    signals: [
      { tone: 'green', text: '2-year vaccines due.' },
      { tone: 'amber', text: 'M-CHAT-R developmental screen overdue.' },
    ],
    smart_prep: ['Growth chart percentiles', 'Vaccine history', 'Milestones'],
    sample_assessment: 'Healthy 2-year-old; growth and development appropriate for age.',
    sample_plan: '18-month vaccines administered. Well-child follow-up at 24 months. Guidance reviewed.',
  },
  {
    id: 'psychiatry', label: 'Psychiatry / Mental Health', short: 'Psych',
    mode: 'mental',
    tagline: 'Psychiatric & behavioral health',
    vocab: ['MSE', 'PHQ-9', 'GAD-7', 'C-SSRS', 'SI/HI', 'affect', 'thought process'],
    example_phrases: [
      'MDD, recurrent, moderate — PHQ-9 = 14.',
      'No SI/HI; safety plan reviewed and in place.',
      'Continues sertraline 100 mg; tolerating well.',
    ],
    common_cpts: [
      { code: '99214', desc: 'Est. patient · moderate complexity' },
      { code: '90833', desc: 'Psychotherapy 30 min add-on' },
      { code: '90836', desc: 'Psychotherapy 45 min add-on' },
      { code: 'H0031', desc: 'Mental health assessment' },
    ],
    signals: [
      { tone: 'amber', text: 'Suicide risk not assessed in this session.' },
      { tone: 'blue',  text: 'PHQ-9 not administered today.' },
    ],
    smart_prep: ['Last PHQ-9 / GAD-7', 'Current psychotropics', 'Safety plan status', 'Prior admissions'],
  },
  S({
    id: 'general_surgery', label: 'General Surgery', short: 'Surgery',
    mode: 'surgical',
    tagline: 'Outpatient surgical consults',
    vocab: ['ASA class', 'METs', 'incisional', 'POD', 'wound status'],
    example_phrases: ['ASA II, functional capacity ≥4 METs.', 'POD 7 — wound healing by primary intention.'],
    common_cpts: [{ code: '99204', desc: 'New patient · moderate complexity' }, { code: '47562', desc: 'Laparoscopic cholecystectomy' }],
    signals: [{ tone: 'amber', text: 'Pre-op anesthesia consult recommended for ASA III+.' }],
    smart_prep: ['Surgical history', 'Current anticoags', 'Last imaging'],
  }),
  S({
    id: 'other', label: 'Other (Primary Care)', short: 'Other',
    tagline: 'General primary care',
    vocab: [], example_phrases: [],
    common_cpts: [{ code: '99213', desc: 'Est. patient · 15 min' }, { code: '99214', desc: 'Est. patient · moderate complexity' }],
    signals: [], smart_prep: ['Reason for visit', 'Current medications'],
  }),
];

export const getSpecialty = (id) => SPECIALTIES.find((s) => s.id === id) || SPECIALTIES[0];

export const SPECIALTY_STORAGE_KEY = 'lumina_specialty';

export const loadSpecialtyId = () => {
  try { return localStorage.getItem(SPECIALTY_STORAGE_KEY) || 'internal_medicine'; }
  catch (_) { return 'internal_medicine'; }
};

export const saveSpecialtyId = (id) => {
  try { localStorage.setItem(SPECIALTY_STORAGE_KEY, id); } catch (_) {}
};
